import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type { InferRequestType } from "hono/client";
import client from "../../utils/api-client";

// types
type OrdersGetAllRoute = typeof client.orders.index.$get;
type GetAllInput = InferRequestType<OrdersGetAllRoute>["query"];

type GetOrdersByUserRoute = (typeof client.orders.user)[":userId"]["$get"];
type GetOrdersByUserIdInput = InferRequestType<GetOrdersByUserRoute>["param"];

type OrdersRouteWithId = (typeof client.orders)[":orderId"];

type GetOrderByIdRoute = OrdersRouteWithId["$get"];
type GetOrderByIdInput = InferRequestType<GetOrderByIdRoute>["param"];

type CreateOrderRoute = (typeof client.orders.index)["$post"];
type CreateOrderInput = InferRequestType<CreateOrderRoute>["json"];

type UpdateOrderStatusRoute = OrdersRouteWithId["status"]["$put"];
type UpdateOrderStatusInput = InferRequestType<UpdateOrderStatusRoute>;

type CancelOrderRoute = OrdersRouteWithId["cancel"]["$post"];
type CancelOrderInput = InferRequestType<CancelOrderRoute>["param"];

type SearchOrdersRoute = (typeof client.orders.search)["$get"];
type SearchOrdersInput = InferRequestType<SearchOrdersRoute>["query"];

export type OrdersApiRequestTypes = {
	getAll: GetAllInput;
	getAllInfinite: GetAllInput;
	getByUserId: GetOrdersByUserIdInput;
	getById: GetOrderByIdInput;
	searchByItem: SearchOrdersInput;
	create: CreateOrderInput;
	updateStatus: UpdateOrderStatusInput;
	cancel: CancelOrderInput;
};

export type OrdersApiResponseTypes = {
	getAll: Awaited<ReturnType<typeof useGetAllOrdersQuery>>["data"];
	getAllInfinite: Awaited<
		ReturnType<typeof useGetAllOrdersInfiniteQuery>
	>["data"];
	getByUserId: Awaited<ReturnType<typeof useGetOrdersByUserQuery>>["data"];
	getById: Awaited<ReturnType<typeof useGetOrderByIdQuery>>["data"];
	searchByItem: Awaited<ReturnType<typeof useSearchOrdersQuery>>["data"];
	create: Awaited<ReturnType<typeof useCreateOrderMutation>>["data"];
	updateStatus: Awaited<
		ReturnType<typeof useUpdateOrderStatusMutation>
	>["data"];
	cancel: Awaited<ReturnType<typeof useCancelOrderMutation>>["data"];
};
// queries
export function useGetOrderStatusesQuery() {
	return useQuery({
		queryKey: ["orders", "statuses"],
		queryFn: async () => {
			const result = await client.orders.statuses.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.message);
			return parsed.data;
		},
		staleTime: 1000 * 60 * 60, // statuses rarely change
	});
}

export function useGetAllOrdersQuery(query: GetAllInput) {
	return useQuery({
		queryKey: ["orders", query],
		queryFn: async () => {
			const result = await client.orders.index.$get({
				query: query,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
	});
}

export function useGetAllOrdersInfiniteQuery(query: GetAllInput) {
	return useInfiniteQuery({
		queryKey: ["orders", "infinite", query],
		queryFn: async ({ pageParam }) => {
			const result = await client.orders.index.$get({
				query: {
					page: Number(pageParam),
					limit: Number(query.limit),
				},
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		getNextPageParam: (lastPage) => {
			if (lastPage.page < lastPage.totalPages) {
				return String(lastPage.page + 1);
			}
			return undefined;
		},
		initialPageParam: "1",
	});
}

export function useGetOrdersByUserQuery(param: GetOrdersByUserIdInput) {
	return useQuery({
		queryKey: ["orders", "user", param.userId],
		queryFn: async () => {
			const result = await client.orders.user[":userId"].$get({
				param,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!param.userId,
	});
}

export function useGetOrderByIdQuery(param: GetOrderByIdInput) {
	return useQuery({
		queryKey: ["orders", param.orderId],
		queryFn: async () => {
			const result = await client.orders[":orderId"].$get({
				param,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!param.orderId,
	});
}

export function useSearchOrdersQuery(query: SearchOrdersInput) {
	return useQuery({
		queryKey: ["orders", "search", query],
		queryFn: async () => {
			const result = await client.orders.search.$get({
				query,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!query.q && query.q.trim().length >= 2,
	});
}

//mutations
export function useCreateOrderMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateOrderInput) => {
			const result = await client.orders.index.$post({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["orders"] });
			queryClient.invalidateQueries({
				queryKey: ["orders", "user", variables.userId],
			});
			queryClient.invalidateQueries({
				queryKey: ["cart", variables.userId],
			});
		},
	});
}

export function useUpdateOrderStatusMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (
			input: UpdateOrderStatusInput["param"] & UpdateOrderStatusInput["json"],
		) => {
			const result = await client.orders[":orderId"].status.$put({
				param: { orderId: input.orderId },
				json: { status: input.status },
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["orders"] });
			queryClient.invalidateQueries({
				queryKey: ["orders", variables.orderId],
			});
		},
	});
}

export function useCancelOrderMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (param: CancelOrderInput) => {
			const result = await client.orders[":orderId"].cancel.$post({
				param,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["orders"] });
			queryClient.invalidateQueries({
				queryKey: ["orders", variables.orderId],
			});
			queryClient.invalidateQueries({ queryKey: ["notifications"] });
		},
	});
}
